import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface AutoMLJob {
  id: string
  name: string
  task: string
  models_tried: number
  best_model: string
  best_score: number
  status: string
  duration: string
}

interface FeatureImportance {
  feature: string
  importance: number
  type: string
}

interface Statistics {
  automl_jobs_mtd: number
  models_generated: number
  best_models_selected: number
  avg_job_duration_hrs: number
  feature_importance_computed: number
  time_saved_hrs: number
}

export default function AutoMLPage() {
  const [jobs, setJobs] = useState<AutoMLJob[]>([])
  const [features, setFeatures] = useState<FeatureImportance[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [jobsRes, featuresRes, statsRes] = await Promise.all([
        fetch('/api/automl/jobs'),
        fetch('/api/automl/feature-importance'),
        fetch('/api/automl/statistics')
      ])
      if (jobsRes.ok) setJobs(await jobsRes.json())
      if (featuresRes.ok) setFeatures(await featuresRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching AutoML:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    if (status === 'Completed') return 'bg-green-100 text-green-800'
    if (status === 'Running') return 'bg-blue-100 text-blue-800'
    if (status === 'Queued') return 'bg-yellow-100 text-yellow-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">AutoML</h1>
          <p className="mt-2 text-gray-600">Phase 278 - Automated machine learning</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Jobs MTD</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.automl_jobs_mtd}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Models Generated</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.models_generated}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Best Selected</p>
              <p className="text-2xl font-bold text-green-600">{statistics.best_models_selected}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Duration</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_job_duration_hrs}h</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Features Computed</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.feature_importance_computed}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Time Saved</p>
              <p className="text-2xl font-bold text-green-600">{statistics.time_saved_hrs}h</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Feature Importance</h2>
          </div>
          <div className="p-6 space-y-3">
            {features.map((feat, idx) => (
              <div key={idx} className="flex items-center">
                <div className="w-48 font-mono text-sm text-gray-700">{feat.feature}</div>
                <div className="flex-1 mx-4">
                  <div className="h-4 bg-gray-200 rounded-full">
                    <div className="h-4 bg-blue-500 rounded-full" style={{width: `${feat.importance * 100}%`}}></div>
                  </div>
                </div>
                <div className="w-16 text-right text-sm font-medium">{(feat.importance * 100).toFixed(0)}%</div>
                <span className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">{feat.type}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">AutoML Jobs</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Models Tried</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Best Model</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Best Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {jobs.map((job) => (
                  <tr key={job.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{job.name}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{job.task}</span>
                    </td>
                    <td className="px-6 py-4 text-purple-600">{job.models_tried}</td>
                    <td className="px-6 py-4 text-green-600 font-medium">{job.best_model}</td>
                    <td className="px-6 py-4 text-blue-600 font-medium">{job.best_score}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(job.status)}`}>{job.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
